"use client";

import dayjs from "dayjs";
import React from "react";
import useMeasure from "react-use-measure";
import uPlot from "uplot";
import UplotReact from "uplot-react";

import { enNames, zhNames } from "@/utils/uplot-plugins/constant";
import { uplotTooltipPlugin } from "@/utils/uplot-plugins/tooltip-plugin";

import { UAreaLineChartProps } from "./UAreaLineChartProps";
import { areaTooltipContent } from "./utils/area-tooltip-content";

const UAreaLineChart = ({
  data,
  strokeColor = "#0369A1",
  strokeWidth = 1,
  dateLabel = "Time",
  label = "Value",
  tooltipLabel,
  valueFormatter = (val) => val,
  lang = "zh",
  showTooltip = false,
  showLegend = "static",
  areaColors = ["#EF4444", "#FCA5A5", "#E4E4E7", "#9EF2BD", "#22C55E"],
  areaLabels = ["1 分", "2 分", "3 分", "4 分", "5 分"],
  tooltipLabelColors = ["white", "black", "black", "black", "white"],
  minY,
  maxY,
}: UAreaLineChartProps) => {
  const [ref, { width: fullWidth, height: fullHeight }] = useMeasure();
  if (!data.length) return <></>;

  return (
    <figure className="h-full w-full" ref={ref} aria-label="historical rating">
      <UplotReact
        data={data}
        options={{
          width: fullWidth,
          height: fullHeight,
          cursor: { y: false },
          fmtDate: (tpl) =>
            uPlot.fmtDate(tpl, lang === "en" ? enNames : zhNames),
          series: [
            {
              value: (_u, v) => dayjs.unix(v).format("YYYY/MM/DD"),
              label: dateLabel,
            },
            ...(areaLabels.map((label, index) => ({
              label,
              points: { show: false },
              stroke: areaColors[index],
              fill: areaColors[index] + "CC",
              paths: uPlot.paths.stepped?.({
                align: -1,
                alignGaps: -1,
              }),
            })) as uPlot.Series[]),
            {
              label,
              points: { show: false },
              stroke: strokeColor,
              width: strokeWidth,
              value: (_u, v) => (valueFormatter(v) as string) ?? v,
            },
          ],
          axes: [
            {
              grid: { show: false },
              ticks: { show: false },
              gap: 12,
            },
            {
              grid: { show: false },
              ticks: { show: false },
              values: (_u, values) =>
                values.map((v) => (valueFormatter(v) as string) ?? v),
            },
          ],
          scales: {
            y: {
              range: () => [minY, maxY],
            },
          },
          legend: {
            show: showLegend !== "none",
            live: showLegend === "live",
            isolate: false,
            markers: {
              fill: (_u, idx) =>
                idx > 0 && idx < data.length - 2
                  ? areaColors[idx - 1]
                  : strokeColor,
            },
          },
          plugins: [
            ...(showTooltip
              ? [
                  uplotTooltipPlugin(
                    areaTooltipContent({
                      priceIndex: data.length - 2,
                      priceLabel: tooltipLabel,
                      areaColors,
                      scoreLabels: areaLabels,
                      scoreColors: tooltipLabelColors,
                      valueFormatter,
                    })
                  ),
                ]
              : []),
          ],
        }}
      />
    </figure>
  );
};

export default React.memo(UAreaLineChart);
